/**
 * @fileoverview Landing page shown to unauthenticated visitors.
 * Displays the COGSLOG brand with Sign In and Create Account options.
 * Authenticated users are redirected to the root route (OrgPicker).
 */

import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

/**
 * Landing page component.
 * Shows a branded card with sign in and registration options.
 * If the user is already authenticated, redirects to /.
 *
 * @returns {JSX.Element} The landing page or a redirect
 */
function Landing() {
  const { authenticated } = useAuth();
  const navigate = useNavigate();
  const [navigating, setNavigating] = useState(false);

  // Authenticated users go straight to the org picker / dashboard
  if (authenticated) {
    return <Navigate to="/" replace />;
  }

  /**
   * Handle Sign In button click — navigates to the custom login page.
   */
  const handleSignIn = () => {
    navigate('/login');
  };

  /**
   * Handle Create Account button click.
   * Checks registration status first — navigates to /register-access if an
   * access code is required, otherwise navigates directly to /signup.
   */
  const handleCreateAccount = async () => {
    setNavigating(true);
    try {
      const res = await api.getRegistrationStatus();
      if (res.data.requires_code) {
        navigate('/register-access');
      } else {
        navigate('/signup');
      }
    } catch {
      // On error, default to signup
      navigate('/signup');
    } finally {
      setNavigating(false);
    }
  };

  return (
    <div className="landing-page">
      <div className="landing-card">
        <div className="landing-header">
          <a href="https://cogslog.com" aria-label="COGSLOG home">
            <img
              src="/logos/stacked/cogslog-stacked-tagline-color.svg"
              alt="COGSLOG"
              className="auth-logo"
            />
          </a>
          <h1 className="landing-title">COGSLOG</h1>
          <p className="landing-subtitle">
            Professional COGS management for restaurant groups
          </p>
        </div>

        <div className="landing-actions">
          <button
            type="button"
            className="btn btn-primary landing-btn"
            onClick={handleSignIn}
          >
            Sign In
          </button>
          <button
            type="button"
            className="btn btn-secondary landing-btn"
            onClick={handleCreateAccount}
            disabled={navigating}
          >
            {navigating ? 'Loading...' : 'Create Account'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Landing;
